import Big from 'big.js';
import { numberUtil, loanUtil } from '@figure/frontlib-util';

// from https://github.com/FigureTechnologies/provenance-frontend-member/blob/develop/src/utils/index.js#L9
export const bigNum = (num) => (!num || isNaN(num) ? new Big(0) : new Big(num));

export const DAYS_IN_A_YEAR = 365;
export const MONTHS_IN_A_YEAR = 12;

/**
 * Get monthly payment
 *
 * @param {Number} totalLoanAmount
 * @param {Number} rate
 * @param {Number} term
 *
 * @returns {Number} JavaScript number (user-facing), monthly payment
 */
export const getMonthlyPayment = (totalLoanAmount, rate, term) => {
  if (!term) return 0; // if term is 0, this function will end up trying to divide by 0

  const monthlyRate = bigNum(rate).div(MONTHS_IN_A_YEAR);
  const termMonths = term * MONTHS_IN_A_YEAR; // Big.js exponents must be JS numbers
  const halfTerm = termMonths / 2; // for optimization of power
  const halfRateExponentialTerm = Math.pow(1 + Number(monthlyRate), halfTerm);
  const rateExponentialTerm = halfRateExponentialTerm * halfRateExponentialTerm;

  return Number(
    bigNum(totalLoanAmount)
      .times(monthlyRate.times(bigNum(rateExponentialTerm)))
      .div(bigNum(rateExponentialTerm).minus(1))
      .toFixed(6)
  );
};

/**
 *
 * Get origination fee
 *
 * @param {Number} totalLoanAmount
 * @param {Number} originationFeePercentage
 *
 * @returns {Number} JavaScript number, origination fee
 */
export const getOriginationFee = (totalLoanAmount, originationFeePercentage) =>
  Number(bigNum(totalLoanAmount).times(bigNum(originationFeePercentage)));

/**
 *
 * Get maximum loan amount from offers.
 *
 * @param {object} offers fixedOffers.offers
 *
 * @returns {Number} max amount rounded down
 */
export const calculateMaximumAmount = (offers) => {
  const drawAmount = offers.reduce((acc, offer) => {
    if (acc < offer.max) return offer.max;
    return acc;
  }, 0);

  return numberUtil.roundDownToMultiple(drawAmount, 1);
};

/**
 *
 * Get minimum loan amount from offers.
 *
 * @param {object} offers fixedOffers.offers
 *
 * @returns {Number} min amount rounded down
 */
export const calculateMinimumAmount = (offers) => {
  const [firstOffer = { min: 0 }] = offers;

  const minAmount = offers.reduce((acc, offer) => {
    if (acc > offer.min) return offer.min;
    return acc;
  }, firstOffer.min);

  return numberUtil.roundUpToMultiple(minAmount, 1);
};

/**
 * Get the daily interest
 *
 * @param {Number} totalLoanAmount
 * @param {Number} rate - the interest rate
 *
 * @returns {Big} number, daily simple interest
 */
export const getDailySimpleInterest = (totalLoanAmount, rate) => bigNum(rate).times(bigNum(totalLoanAmount)).div(DAYS_IN_A_YEAR);

/**
 *
 * @param {Number} totalLoanAmount
 * @param {Number} aprFeeTotal
 *
 * For the purpose of APR capitalized fees are removed from the present loan value
 */
export const getPresentLoanValue = (totalLoanAmount, aprFeeTotal) => totalLoanAmount - aprFeeTotal;

/**
 *
 * @param {Number} termInMonths
 * @param {Number} monthlyPmt
 * @param {Number} presentLoanValue
 *
 * @returns {String} APR, result is displayed to user
 */
export const getAPR = (termInMonths, monthlyPmt, presentLoanValue) =>
  Number(rate(termInMonths, monthlyPmt, presentLoanValue, 0, 'end', 0.1, 1e-6, 100) * 12).toFixed(5);

/**
 * Calculates loan data.
 *
 * @param {Number} loanAmount
 * @param {Number} baseRate
 * @param {Number} term
 * @param {Number} originationFeePercentage
 *
 * @returns {Object} apr, monthlyPayment
 */
export const getLoanData = (loanAmount, baseRate, term, originationFeePercentage) => {
  const totalLoanAmount = loanUtil.getTotalAmountFromDraw(loanAmount, originationFeePercentage);
  const monthlyPayment = getMonthlyPayment(totalLoanAmount, baseRate, term);
  const aprFees = loanAmount * originationFeePercentage;
  const invertedMonthlyPayment = monthlyPayment * -1;
  const presentLoanValue = getPresentLoanValue(totalLoanAmount, aprFees);
  const apr = getAPR(term * 12, invertedMonthlyPayment, presentLoanValue);

  return {
    apr,
    monthlyPayment,
  };
};
/**
 *
 * @param {Number} nper
 * @param {Number} pmt
 * @param {Number} pv
 * @param {Number} fv
 * @param {String} whenPmt
 * @param {Number} guess
 * @param {Number} tol
 * @param {Number} maxIter
 *
 * Compute the rate of interest per period. (Javascript version of the rate function in numpy and excel)
 *
 * nper : Number of compounding periods
 * pmt : Monthly payment
 * pv : Present value
 * fv : Future value
 * whenPmt : {{'begin', 1}, {'end', 0}}, {string, int}
 *    When payments are due ('begin' (1) or 'end' (0))
 * guess : Number
 *    Starting guess for solving the rate of interest, default 0.1
 * tol : Number
 *    Required tolerance for the solution, default 1e-6
 * maxiter : int
 *    Maximum iterations in finding the solution
 *
 * The rate of interest is computed by iteratively solving the (non-linear) equation::
 *    fv + pv*(1+rate)**nper + pmt*(1+rate*when)/rate * ((1+rate)**nper - 1) = 0
 *    for ``rate``.
 *
 *  References
 *  ----------
 *  Wheeler, D. A., E. Rathke, and R. Weir (Eds.) (2009, May). Open Document
 *  Format for Office Applications (OpenDocument)v1.2, Part 2: Recalculated
 *  Formula (OpenFormula) Format - Annotated Version, Pre-Draft 12.
 *  Organization for the Advancement of Structured Information Standards
 *  (OASIS). Billerica, MA, USA. [ODT Document]. Available:
 *  http://www.oasis-open.org/committees/documents.php?wg_abbrev=office-formula
 *  OpenDocument-formula-20090508.od
 */
export const rate = (nper, pmt, pv, fv, whenPmt, guess, tol, maxIter) => {
  const whenMap = { begin: 1, end: 0 };
  const whenPmtNumeric = whenMap[whenPmt];

  let rn = guess;
  let iterator = 0;
  let close = false;
  while (iterator < maxIter && !close) {
    const rnp1 = rn - gDivGp(rn, nper, pmt, pv, fv, whenPmtNumeric);
    const diff = Math.abs(rnp1 - rn);
    close = diff < tol;
    iterator++;
    rn = rnp1;
  }

  if (!close) {
    throw new Error('Selected tolerance not met. Select a higher tolerance.');
  } else {
    return rn;
  }
};

/**
 *
 * @param {Number} r
 * @param {Number} n
 * @param {Number} p
 * @param {Number} x
 * @param {Number} y
 * @param {Number} w
 *
 * Use Newton's iteration until the change is less than tol
 * for all values or a maximum number of iterations is reached.
 * Newton's rule is
 *    r_{n+1} = r_{n} - g(r_n)/g'(r_n)
 * where
 *    g(r) is the formula
 *    g'(r) is the derivative with respect to r.
 */
export const gDivGp = (r, n, p, x, y, w) => {
  const t1 = (r + 1) ** n;
  const t2 = (r + 1) ** (n - 1);
  return (
    (y + t1 * x + (p * (t1 - 1) * (r * w + 1)) / r) /
    (n * t2 * x - (p * (t1 - 1) * (r * w + 1)) / r ** 2 + (n * p * t2 * (r * w + 1)) / r + (p * (t1 - 1) * w) / r)
  );
};
